import React from "react";
import { Trans, useTranslation } from "react-i18next";
import AuthService from "src/services/authService";
import { ConfirmDialog } from "src/ui/containers/dashboard/devices/components/shared";
import { DangerOutlinedButton } from "src/ui/shared/CustomButton";
import { MinimalTableCell } from "src/ui/shared/table/MinimalTableComponents";

import { useDeleteFdroidRepo } from "@admin/hooks/useDeleteFdroidRepo";
import { DialogContentText } from "@mui/material";
import { HoverRow } from "@shared/table/HoverRow";
import { flexRender, Row } from "@tanstack/react-table";

import { useStyles } from "./AdminFdroidPage.style";

type TableItemProps = {
    row: Row<API.FdroidRepoAdmin>;
    onDeleteSuccess: () => void;
};

const TableItem: React.FC<TableItemProps> = ({ row, onDeleteSuccess }) => {
    const { t } = useTranslation(["pages", "modals", "common"]);
    const { classes } = useStyles();
    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const { isLoading, onMutate } = useDeleteFdroidRepo();
    const isAdmin = AuthService.isUserAdmin();
    const repo: API.FdroidRepoAdmin = row.original;

    const handleConfirm = () => {
        onMutate(repo.id, () => {
            setDeleteDialogOpen(false);
            onDeleteSuccess();
        });
    };

    return (
        <React.Fragment>
            <HoverRow clickable={false}>
                {row
                    .getVisibleCells()
                    .map((cell) => flexRender(cell.column.columnDef.cell, { ...cell.getContext(), key: cell.id }))}
                <MinimalTableCell align="right">
                    <div className={classes.actionCell}>
                        <DangerOutlinedButton
                            onClick={() => setDeleteDialogOpen(true)}
                            disabled={!isAdmin}
                            loading={isLoading}
                            tooltipProps={{
                                title: !isAdmin ? t("pages:adminFdroidPage.deleteTooltipNoPermission") : undefined,
                            }}
                        >
                            <i className="fa-solid fa-trash" />
                        </DangerOutlinedButton>
                    </div>
                </MinimalTableCell>
            </HoverRow>
            <ConfirmDialog
                title={t("modals:deleteFdroidRepo.title", { name: repo.name })}
                dangerButton
                primaryActionText={t("common:button.delete")}
                open={deleteDialogOpen}
                onConfirm={handleConfirm}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogContentText>
                    <Trans
                        ns="modals"
                        i18nKey="deleteFdroidRepo.description"
                        values={{ name: repo.name }}
                        components={{ bold: <strong /> }}
                    />
                </DialogContentText>
            </ConfirmDialog>
        </React.Fragment>
    );
};

export default TableItem;
